import { useState, useEffect } from "react";
import { AppSidebar } from "./AppSidebar";
import { ChatBot } from "./ChatBot";
import { useTokenRefresh } from "../hooks/useTokenRefresh";
import Header from "./Header";

interface LayoutProps {
  children: React.ReactNode;
}

function useMediaQuery(query: string) {
  const [matches, setMatches] = useState<boolean>(() =>
    typeof window !== "undefined" ? window.matchMedia(query).matches : false
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const m = window.matchMedia(query);
    const onChange = () => setMatches(m.matches);
    try { m.addEventListener("change", onChange); } catch { m.addListener(onChange as any); }
    return () => {
      try { m.removeEventListener("change", onChange); } catch { m.removeListener(onChange as any); }
    };
  }, [query]);
  return matches;
}

export function Layout({ children }: LayoutProps) {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);

  // Auto-collapse sidebar on mobile, expand on desktop
  useEffect(() => {
    if (isMobile) {
      setSidebarCollapsed(true); // Always start collapsed on mobile
    } else {
      setSidebarCollapsed(false); // Always start expanded on desktop
    }
  }, [isMobile]);

  // Setup automatic token refresh
  useTokenRefresh();

  return (
    <div className="flex h-screen w-full bg-background">
      {/* Sidebar */}
      <AppSidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        <Header
          onSidebarToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
          sidebarCollapsed={sidebarCollapsed}
        />

        {/* Page Content */}
        <main className="flex-1 overflow-auto">
          <div className="min-h-full">
            {children}
          </div>
        </main>
      </div>

      {/* ChatBot overlay */}
      <ChatBot />
    </div>
  );
}
